import { useRouter } from 'next/router';
import { useCallback } from 'react';

import type { TradeDirection } from '@/components/TransactionModule/types';
import { isSameQuery } from '@/components/TransactionModule/utils';

import { useTokenResult } from '@/hooks/tokenQueries/useTokenResult';
import { useInitializeDefaultTokens } from '@/hooks/useInitializeDefaultTokens';
import { useSelectedTokens } from '@/hooks/useSelectedTokens';

import { TokenBase } from '@/utils/0x/token-registry.types';
import { ChainAddress } from '@/utils/models';
import { getTokenInfoFromQueryParameter } from '@/utils/url';

/** Pages whose query string carries the trade module's pair. */
type TokenPairPathname = '/' | '/trade' | '/swap';

/**
 * URL-driven token pair for a page hosting the trade module: the pair lives
 * in the page's query string, user actions push history entries, and empty
 * fields fill with the default pair.
 */
export const useUrlTokenPair = (pathname: TokenPairPathname) => {
  const router = useRouter();
  const { push, replace, query } = router;

  const { buyTokenAddress, buyTokenChainId, sellTokenAddress, sellTokenChainId } =
    useSelectedTokens();

  const { token: sellToken } = useTokenResult(sellTokenAddress, sellTokenChainId);
  const { token: buyToken } = useTokenResult(buyTokenAddress, buyTokenChainId);

  const buildTokenQuery = useCallback(
    (
      newBuyAddress?: ChainAddress,
      newBuyChain?: number,
      newSellAddress?: ChainAddress,
      newSellChain?: number,
    ) => {
      const { buyAddress, buyChain, sellAddress, sellChain, ...otherParams } = query;
      const newQuery = { ...otherParams };

      if (newBuyAddress) newQuery.buyAddress = newBuyAddress;
      if (newBuyChain) newQuery.buyChain = newBuyChain.toString();
      if (newSellAddress) newQuery.sellAddress = newSellAddress;
      if (newSellChain) newQuery.sellChain = newSellChain.toString();

      return newQuery;
    },
    [query],
  );

  // User actions (select/flip) push so Back undoes them one at a time.
  const syncUrlParameters = useCallback(
    (
      newBuyAddress?: ChainAddress,
      newBuyChain?: number,
      newSellAddress?: ChainAddress,
      newSellChain?: number,
    ) => {
      const newQuery = buildTokenQuery(newBuyAddress, newBuyChain, newSellAddress, newSellChain);
      if (isSameQuery(newQuery, query)) return;
      push({ pathname, query: newQuery }, undefined, { shallow: true });
    },
    [buildTokenQuery, pathname, push, query],
  );

  // Hook-initiated fills replace so the automatic default pair never adds a
  // history entry - Back must leave the page, not strip the params and
  // re-trigger the fill (this also keeps the widget iframe from polluting the
  // embedder's session history on load).
  const replaceUrlParameters = useCallback(
    (
      newBuyAddress?: ChainAddress,
      newBuyChain?: number,
      newSellAddress?: ChainAddress,
      newSellChain?: number,
    ) => {
      const newQuery = buildTokenQuery(newBuyAddress, newBuyChain, newSellAddress, newSellChain);
      if (isSameQuery(newQuery, query)) return;
      replace({ pathname, query: newQuery }, undefined, { shallow: true });
    },
    [buildTokenQuery, pathname, replace, query],
  );

  // Parse token params so malformed entry URLs still receive a default pair.
  // The initializer captures this decision once the router is ready.
  const followWalletChain =
    !getTokenInfoFromQueryParameter(query.buyAddress, query.buyChain) &&
    !getTokenInfoFromQueryParameter(query.sellAddress, query.sellChain);

  // Fill any empty token field with the static default pair. While the pair
  // is untouched it follows the connected wallet's chain.
  const { setShouldInitialize } = useInitializeDefaultTokens({
    buyTokenAddress,
    buyTokenChainId,
    sellTokenAddress,
    sellTokenChainId,
    syncUrlParameters: replaceUrlParameters,
    followWalletChain,
  });

  const handleFlipSelectedTokens = useCallback(() => {
    setShouldInitialize(false);
    syncUrlParameters(sellTokenAddress, sellTokenChainId, buyTokenAddress, buyTokenChainId);
  }, [
    buyTokenAddress,
    buyTokenChainId,
    sellTokenAddress,
    sellTokenChainId,
    syncUrlParameters,
    setShouldInitialize,
  ]);

  const handleSelectToken = useCallback(
    (selectedToken: Pick<TokenBase, 'address' | 'chainId'>, side: TradeDirection) => {
      const { address, chainId } = selectedToken;

      if (side === 'sell') {
        setTimeout(() => document.getElementById('sellAmount')?.focus(), 0);
      }

      setShouldInitialize(false);

      if (side === 'buy') {
        syncUrlParameters(address, chainId, sellTokenAddress, sellTokenChainId);
      } else {
        syncUrlParameters(buyTokenAddress, buyTokenChainId, address, chainId);
      }
    },
    [
      buyTokenAddress,
      buyTokenChainId,
      syncUrlParameters,
      sellTokenAddress,
      sellTokenChainId,
      setShouldInitialize,
    ],
  );

  const handleSelectPair = useCallback(
    (nextBuyToken: TokenBase, nextSellToken: TokenBase) => {
      setShouldInitialize(false);
      syncUrlParameters(
        nextBuyToken.address,
        nextBuyToken.chainId,
        nextSellToken.address,
        nextSellToken.chainId,
      );
    },
    [syncUrlParameters, setShouldInitialize],
  );

  return {
    buyToken,
    sellToken,
    buyTokenAddress,
    buyTokenChainId,
    sellTokenAddress,
    sellTokenChainId,
    handleFlipSelectedTokens,
    handleSelectToken,
    handleSelectPair,
  };
};
